import React, { useState, useEffect } from 'react';
import { API_CONFIG } from '../../config/api';
import { getHeaders } from '../../utils/api';
import './CountryManagement.css';

interface Country {
  id: number;
  code: string;
  name: string;
  flag: string | null;
  languageCode: string;
}

const CountryManagement = () => {
  const [countries, setCountries] = useState<Country[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    flag: '',
    languageCode: ''
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchCountries();
  }, []);

  const fetchCountries = async () => {
    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}/admin/countries`, {
        headers: getHeaders()
      });
      if (!response.ok) {
        throw new Error('Failed to fetch countries');
      }
      const data = await response.json();
      setCountries(data);
    } catch (err) {
      setError('Failed to load countries');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}/admin/countries/add`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
          id: 0,
          code: formData.code.toUpperCase(),
          name: formData.name,
          flag: formData.flag || null,
          languageCode: formData.languageCode
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to add country');
      }

      await fetchCountries();
      setFormData({ code: '', name: '', flag: '', languageCode: '' });
      setShowAddModal(false);
    } catch (err) {
      setError('Failed to add country');
      console.error('Error:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  if (loading) {
    return <div className="loading">Loading countries...</div>;
  }

  return (
    <div className="content-section" style={{ minWidth: '50vw' }}>
      <div className="content-header">
        <h2>Country Management</h2>
        <button className="add-button" onClick={() => setShowAddModal(true)}>Add Country</button>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="country-table-container">
        <table className="country-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Code</th>
              <th>Name</th>
              <th>Flag</th>
              <th>Language Code</th>
            </tr>
          </thead>
          <tbody>
            {countries.map(country => (
              <tr key={country.id}>
                <td>{country.id}</td>
                <td>{country.code}</td>
                <td>{country.name}</td>
                <td>{country.flag || '-'}</td>
                <td>{country.languageCode}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showAddModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Add Country</h3>
              <button 
                className="close-button" 
                onClick={() => setShowAddModal(false)}
              >
                ×
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="code">Country Code:</label>
                <input
                  type="text"
                  id="code"
                  name="code"
                  value={formData.code}
                  onChange={handleInputChange}
                  maxLength={2}
                  required
                  placeholder="e.g., DE"
                />
              </div>
              <div className="form-group">
                <label htmlFor="name">Country Name:</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  placeholder="e.g., Germany"
                />
              </div>
              <div className="form-group">
                <label htmlFor="flag">Flag Emoji:</label>
                <input
                  type="text"
                  id="flag"
                  name="flag"
                  value={formData.flag}
                  onChange={handleInputChange}
                  placeholder="e.g., 🇩🇪"
                />
              </div>
              <div className="form-group">
                <label htmlFor="languageCode">Language Code:</label>
                <input
                  type="text"
                  id="languageCode"
                  name="languageCode"
                  value={formData.languageCode}
                  onChange={handleInputChange}
                  required
                  placeholder="e.g., de"
                />
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="cancel-button"
                  onClick={() => setShowAddModal(false)}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="submit-button"
                  disabled={submitting}
                >
                  {submitting ? 'Adding...' : 'Add Country'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CountryManagement; 